import React, { useEffect, useMemo, useState } from 'react';
import { useAppState } from '../../State.tsx';
import { FlowNode, NodeId, ProductAtcData } from '../../Types/Types.ts';
import HorizontalOption from './HorizontalOption.tsx';
import AddToCartButton from '../../../NewFitAssistant/Components/Buttons/AddToCartButton.tsx';

import './ContentOptionStep.less';
import Buttons from '../Buttons/Buttons.tsx';
import PrimaryButton from '../Buttons/PrimaryButton.tsx';
import SecondaryButton from '../Buttons/SecondaryButton.tsx';

type ContentOptionStep = {
  node: FlowNode;
};

const ContentOptionStep = ({ node }: ContentOptionStep) => {
  const { setShowFitAssistant, fitAssistantBikesData } = useAppState();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [atcDataBySelection, setAtcDataBySelection] = useState<Record<string, ProductAtcData>>({});
  const cmsStep = fitAssistantBikesData?.Steps?.find((stepData) => stepData.Parameter === node.cmsParameter);
  const translations = fitAssistantBikesData?.Translations;
  const products = fitAssistantBikesData?.PannierProducts?.slice().sort((a, b) =>
    a.ProductId.localeCompare(b.ProductId)
  );
  const isExtranet = window._THULEDATA?.isExtranet ?? false;
  const selectionKey = useMemo(() => selectedProducts.slice().sort().join(','), [selectedProducts]);
  const cachedAtcData = selectionKey ? atcDataBySelection[selectionKey] : undefined;
  const addToCartButtonData = cachedAtcData?.addToCartButton ?? null;
  const priceContainerData = cachedAtcData?.priceContainer ?? null;
  const hasLoadedAtcData = cachedAtcData?.isLoaded ?? false;
  const shouldShowMissingButtonFallback = useMemo(
    () =>
      !isExtranet &&
      (selectedProducts.length === 0 || (hasLoadedAtcData && (!addToCartButtonData || !priceContainerData))),
    [isExtranet, selectedProducts.length, hasLoadedAtcData, addToCartButtonData, priceContainerData]
  );

  const productsForSection = (section: string) =>
    products?.filter((p) => p.Placements.includes(`${node.cmsParameter}.${section}`));

  const addOrRemoveProduct = (productId: string) => {
    if (!selectedProducts.includes(productId)) {
      setSelectedProducts([...selectedProducts, productId]);
    } else {
      setSelectedProducts(selectedProducts.filter((x) => x !== productId));
    }
  };

  useEffect(() => {
    if (!selectionKey || isExtranet || atcDataBySelection[selectionKey]?.isLoaded) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/sitecore/fitguide/GetFitGuideATCAndPrice', {
          method: 'POST',
          headers: new Headers({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            ProductIds: selectedProducts,
          }),
        });
        const data = await res.json();

        if (!cancelled) {
          setAtcDataBySelection((prev) => ({
            ...prev,
            [selectionKey]: {
              addToCartButton: data?.Button ?? null,
              priceContainer: data?.PriceContainer ?? null,
              isLoaded: true,
            },
          }));
        }
      } catch {
        if (!cancelled) {
          setAtcDataBySelection((prev) => ({
            ...prev,
            [selectionKey]: {
              addToCartButton: null,
              priceContainer: null,
              isLoaded: true,
            },
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectionKey, selectedProducts, isExtranet, atcDataBySelection]);

  return (
    <>
      <div className="content-option-selector d-flex flex-column justify-content-center">
        {cmsStep?.ImageUrl && (
          <div className="cms-image-wrapper d-flex justify-content-center w-100 mb-9">
            <img src={cmsStep.ImageUrl} alt={cmsStep.ImageAlt} />
          </div>
        )}
        {cmsStep?.Text && <div dangerouslySetInnerHTML={{ __html: cmsStep.Text }}></div>}
        <h5 className="mt-10 mb-6">{translations?.addProduct}</h5>
        <div className="options">
          {productsForSection('products')?.map((option, index) => (
            <HorizontalOption
              option={option}
              onClick={() => addOrRemoveProduct(option.ProductId)}
              isSelected={selectedProducts.includes(option.ProductId)}
              key={index}
            />
          ))}
        </div>
        <h5 className="mt-10 mb-6">{translations?.forMoreStability}</h5>
        <div className="options">
          {productsForSection('stability')?.map((option, index) => (
            <HorizontalOption
              option={option}
              onClick={() => addOrRemoveProduct(option.ProductId)}
              isSelected={selectedProducts.includes(option.ProductId)}
              key={index}
            />
          ))}
        </div>
      </div>
      <Buttons visible>
        {!isExtranet && addToCartButtonData && priceContainerData && (
          <AddToCartButton
            addToCartButtonData={addToCartButtonData}
            priceContainer={priceContainerData}
            onComplete={() => setShowFitAssistant(false)}
            disabled={selectedProducts.length === 0}
          />
        )}
        {shouldShowMissingButtonFallback && (
          <PrimaryButton visible disabled text={translations?.addToCart ?? ''} onClick={() => {}} />
        )}
        <SecondaryButton visible text={translations?.close ?? ''} onClick={() => setShowFitAssistant(false)} />
      </Buttons>
    </>
  );
};

export default ContentOptionStep;
